import React, { useState, useRef, useEffect, useContext } from "react"

import { useHistory } from "react-router-dom"
import useSound from "use-sound"

import Popover from "@mui/material/Popover"
import IconButton from "@mui/material/IconButton"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemText from "@mui/material/ListItemText"
import { makeStyles } from "@mui/styles"

import TicketListItem from "../TicketListItem"
import { i18n } from "../../translate/i18n"
import useTickets from "../../hooks/useTickets"
import alertSound from "../../assets/sound.mp3"
import { AuthContext } from "../../context/Auth/AuthContext"
import { SocketContext } from "../../context/Socket/SocketContext"

import { NotificationsNoneOutlined } from "@mui/icons-material"
import { Badge } from "@mui/material"
import api from "../../services/api"

const useStyles = makeStyles((theme) => ({
    tabContainer: {
        overflowY: "auto",
        maxHeight: 350,
        ...theme.scrollbarStyles,
    },
    popoverPaper: {
        width: "100%",
        maxWidth: 350,
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(1),
        [theme.breakpoints.down("sm")]: {
            maxWidth: 270,
        },
    },
    noShadow: {
        boxShadow: "none !important",
    },
}))

const NotificationsPopOver = () => {
    const classes = useStyles()

    const history = useHistory()
    const { user } = useContext(AuthContext)
    const ticketIdUrl = +history.location.pathname.split("/")[2]
    const ticketIdRef = useRef(ticketIdUrl)
    const anchorEl = useRef()
    const [isOpen, setIsOpen] = useState(false)
    const [notifications, setNotifications] = useState([])
    const { profile, queues } = user

    const { tickets } = useTickets({ withUnreadMessages: "true" })
    const [play] = useSound(alertSound)
    const soundAlertRef = useRef()

    const { socket } = useContext(SocketContext)

    const markNotificationAsRead = (id) => {
        let arr = [...notifications]

        const updated = arr.filter((item) => item.id !== id)

        setNotifications(updated)
    }

    useEffect(() => {
        ;(async () => {
            if ("Notification" in window) {
                try {
                    const permission = await Notification.requestPermission()
                    if (permission === "granted") {
                        import("../../utils/firebaseMessaging").then(
                            async (res) => {
                                const token = await res.manageFCMToken()

                                api.put("/users/fcmToken/update", {
                                    fcmToken: token,
                                })
                                    .then(() => console.log("ok"))
                                    .catch((err) => console.log(err))
                            },
                        )
                    }
                } catch (error) {
                    console.log(error)
                }
            } else {
                console.log("This browser doesn't support notifications")
            }
        })()
    }, [])

    useEffect(() => {
        soundAlertRef.current = play
    }, [play])

    useEffect(() => {
        setNotifications(tickets)
    }, [tickets])

    useEffect(() => {
        ticketIdRef.current = ticketIdUrl
    }, [ticketIdUrl])

    useEffect(() => {
        if (socket?.connected) {
            socket.emit("joinNotification")
        }
    }, [socket])

    useEffect(() => {
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker?.addEventListener("message", (event) => {
                if (event.data?.type === "NEW_MESSAGE") {
                    history.push(event.data.url)
                }
            })
        }
    }, [])

    useEffect(() => {
        const companyId = localStorage.getItem("companyId")

        socket.on(`company-${companyId}-appMessage`, (data) => {
            if (
                data.action === "create" &&
                !data.message.read &&
                (data.ticket.userId === user?.id || !data.ticket.userId)
            ) {
                setNotifications((prevState) => {
                    const ticketIndex = prevState.findIndex(
                        (t) => t.id === data.ticket.id,
                    )
                    if (ticketIndex !== -1) {
                        prevState[ticketIndex] = data.ticket
                        return [...prevState]
                    }
                    return [data.ticket, ...prevState]
                })

                const shouldNotNotificate =
                    (data.message.ticketId === ticketIdRef.current &&
                        document.visibilityState === "visible") ||
                    (data.ticket.userId && data.ticket.userId !== user?.id) ||
                    data.ticket.isGroup

                if (shouldNotNotificate) return

                soundAlertRef.current()
            }
        })
    }, [user, profile, queues, socket])

    const handleClick = () => {
        setIsOpen((prevState) => !prevState)
    }

    const handleClickAway = () => {
        setIsOpen(false)
    }

    const NotificationTicket = ({ children }) => {
        return <div onClick={handleClickAway}>{children}</div>
    }

    return (
        <>
            <IconButton
                onClick={handleClick}
                ref={anchorEl}
                aria-label="Mostrar Notificações"
                variant="contained"
                style={{ color: "white" }}
            >
                <Badge
                    color="success"
                    invisible={notifications.length <= 0}
                    badgeContent={notifications.length}
                >
                    <NotificationsNoneOutlined />
                </Badge>
            </IconButton>
            <Popover
                disableScrollLock
                open={isOpen}
                anchorEl={anchorEl.current}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                classes={{ paper: classes.popoverPaper }}
                onClose={handleClickAway}
                sx={{
                    maxWidth: 550,
                }}
            >
                <List dense className={classes.tabContainer}>
                    {notifications.length === 0 ? (
                        <ListItem>
                            <ListItemText>
                                {i18n.t("notifications.noTickets")}
                            </ListItemText>
                        </ListItem>
                    ) : (
                        notifications.map((ticket) => (
                            <NotificationTicket key={ticket.id}>
                                <TicketListItem
                                    ticket={ticket}
                                    onTicketClick={markNotificationAsRead}
                                />
                            </NotificationTicket>
                        ))
                    )}
                </List>
            </Popover>
        </>
    )
}

export default NotificationsPopOver
